import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Paper, Typography, useMediaQuery } from '@mui/material';
import { publicScoreGame, scoreGame } from '../../../features/game/gameSlice';
import { GameStatus } from '../../../constants/gameConstants';
import ColorLine from '../colorDot/colorLine';

const ScoreControl = ({ game, team, isPublic, onScore, submitting = false }) => {
  const dispatch = useDispatch();
  const scoringGame = useSelector((state) => state.game.scoringGame);
  const currentSet = game.sets?.find((set) => set.id === game.currentSet);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const busy = scoringGame || submitting;

  const [multiplierActive, setMultiplierActive] = React.useState(false);

  const getGameTypePlayers = () => {
    switch (game.gameFormat.gameType) {
      case 2: // Singles
        return 1;
      case 1: // Pairs
        return 2;
      case 3: // Triples
        return 3;
      case 4: // Fours
        return 4;
      default:
        return 1; // Default to singles if unknown
    }
  };

  const maxScorePerEnd = game.gameFormat.numBowls * getGameTypePlayers();

  if (
    game.status === GameStatus.COMPLETE ||
    game.status === GameStatus.PLAYOFF ||
    currentSet?.isDone ||
    game.totalsOnly
  ) {
    return null;
  }

  const multiplierAlreadyUsed = (teamId) => {
    if (game.gameFormat.setAsSkin) {
      // For skin games: check if multiplier has been used anywhere in the entire match
      for (const set of game.sets) {
        if (set.scores && Array.isArray(set.scores)) {
          // Check each score in the scores array
          for (const score of set.scores) {
            if (score.multiplierActive && score.multiplierActive[teamId]) {
              return true;
            }
          }
        }
      }
    } else {
      // For regular games: check if multiplier has been used in the current set only
      if (currentSet && currentSet.scores && Array.isArray(currentSet.scores)) {
        // Check each score in the current set's scores array
        for (const score of currentSet.scores) {
          if (score.multiplierActive && score.multiplierActive[teamId]) {
            return true;
          }
        }
      }
    }
    return false;
  };

  const handleScoreUpdate = async (scoreValue) => {
    if (onScore) {
      try {
        await onScore({ teamId: team.id, scoreValue, multiplierActive });
      } catch (error) {
        // The portal page owns its error presentation; do not leave an
        // unhandled rejection behind if a caller lets one escape.
      }
      if (game.gameFormat.multiplier > 1) {
        setMultiplierActive(false);
      }
      return;
    }

    if (isPublic) {
      await dispatch(
        publicScoreGame({ gameId: game.id, teamId: team.id, scoreValue, multiplierActive })
      ).then(() => {
        if (game.gameFormat.multiplier > 1) {
          setMultiplierActive(false);
        }
      });

      //Reload Page
      window.location.reload();
    } else {
      await dispatch(
        scoreGame({ gameId: game.id, teamId: team.id, scoreValue, multiplierActive })
      ).then((payload) => {
        if (game.gameFormat.multiplier > 1) {
          setMultiplierActive(false);
        }
      });
    }
  };

  const scoreButton = (value, text) => {
    return (
      <Box
        m={!isMobile ? '5px' : '2px'}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <Button
          variant={'contained'}
          color={multiplierActive ? 'primary' : 'background'}
          disabled={busy}
          sx={{
            width: !isMobile ? 40 : 30,
            height: !isMobile ? 40 : 30,
            borderRadius: 3,
            fontWeight: 'bold'
          }}
          onClick={() => handleScoreUpdate(value)}>
          {text}
        </Button>
      </Box>
    );
  };

  const activateMultipler = () => {
    if (game.gameFormat.multiplier > 1) {
      setMultiplierActive(!multiplierActive);
    }
  };

  const multiplierButton = (value) => {
    const isMultiplierUsed = multiplierAlreadyUsed(team.id);

    return (
      <Box
        m={!isMobile ? '5px' : '2px'}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <Button
          variant={'contained'}
          backgroundColor={multiplierActive ? 'background' : 'primary'}
          color={multiplierActive ? 'primary' : 'white'}
          disabled={busy || isMultiplierUsed}
          sx={{
            m: 1,
            borderRadius: 3,
            fontWeight: 'bold',
            border: '2px solid ',
            borderColor: 'primary.main',
            opacity: busy || isMultiplierUsed ? 0.5 : 1,
            '&.Mui-disabled': {
              color: 'grey.600',
              borderColor: 'grey.200',
              cursor: 'not-allowed'
            }
          }}
          onClick={() => activateMultipler()}>
          Power Play
        </Button>
      </Box>
    );
  };

  const scoreZeroButton = () => {
    return (
      <Box
        m={!isMobile ? '5px' : '2px'}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <Button
          variant={'contained'}
          color={'background'}
          elevation={2}
          disabled={busy}
          onClick={() => handleScoreUpdate(0)}
          sx={{ width: '100%', height: !isMobile ? 40 : 30, borderRadius: 3, fontWeight: 'bold' }}>
          {0}
        </Button>
      </Box>
    );
  };

  return (
    <Paper
      sx={{
        mt: 3,
        width: !isMobile ? '234px' : '170px',
        maxWidth: '100%',
        alignSelf: 'center',
        borderRadius: 2,
        p: '5px'
      }}>
      <Box display={'flex'} flexDirection={'column'} alignItems={'center'} pt={1}>
        <ColorLine color={team.color} />
        {game.gameFormat.multiplier && game.gameFormat.multiplier > 1
          ? multiplierButton(game.gameFormat.multiplier)
          : null}
        <Box display={'flex'} flexDirection={'row'} flexWrap={'wrap'} justifyContent={'center'}>
          {Array.from({ length: maxScorePerEnd }, (_, i) =>
            scoreButton(
              multiplierActive ? game.gameFormat.multiplier * (i + 1) : i + 1,
              multiplierActive ? `${i + 1}x${game.gameFormat.multiplier}` : i + 1
            )
          ).map((btn, idx) => (
            <React.Fragment key={idx}>{btn}</React.Fragment>
          ))}
        </Box>
        <Box display={'flex'} flexDirection={'row'} flexWrap={'wrap'}>
          {scoreZeroButton()}
        </Box>
      </Box>
    </Paper>
  );
};

export default ScoreControl;
